import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
export function normalized(text) {
    let value = '';
    const positions = [];
    for(let i = 0; i < text.length; i++){
        const char = text[i].replace(/[“”]/g, '"').replace(/[‘’]/g, "'").replace(/[–—]/g, '-').toLowerCase();
        if (/\s/.test(char)) {
            if (!value || value.endsWith(' ')) continue;
            value += ' ';
        } else {
            value += char;
        }
        for(let part = 0; part < char.length; part++)positions.push(i);
    }
    return {
        value: value.trimEnd(),
        positions
    };
}
// Match the rendered source text, including quotes crossing emphasis or lines.
export function highlightSourceQuote(quote) {
    return ()=>(tree)=>{
            const texts = [];
            let content = '';
            function collect(node, parent) {
                if (node.type === 'text' && node.value && parent) {
                    texts.push({
                        node,
                        parent,
                        start: content.length
                    });
                    content += node.value;
                }
                for (const child of node.children ?? [])collect(child, node);
                if (node.type === 'element' && /^(p|h[1-6]|li|tr|td|th|br)$/.test(node.tagName ?? '')) content += '\n';
            }
            collect(tree);
            const needle = normalized(quote).value.trim();
            if (!needle) return;
            const source = normalized(content);
            const index = source.value.indexOf(needle);
            if (index < 0) return;
            const start = source.positions[index];
            const end = source.positions[index + needle.length - 1] + 1;
            for (const { node, parent, start: nodeStart } of texts.reverse()){
                const from = Math.max(0, start - nodeStart);
                const to = Math.min(node.value?.length ?? 0, end - nodeStart);
                if (from >= to || !node.value || !parent.children) continue;
                const replacement = [];
                if (from) replacement.push({
                    type: 'text',
                    value: node.value.slice(0, from)
                });
                replacement.push({
                    type: 'element',
                    tagName: 'mark',
                    properties: {
                        'data-citation-highlight': 'true',
                        'data-testid': Testids.assistant.sourceHighlight,
                        className: 'rounded'
                    },
                    children: [
                        {
                            type: 'text',
                            value: node.value.slice(from, to)
                        }
                    ]
                });
                if (to < node.value.length) replacement.push({
                    type: 'text',
                    value: node.value.slice(to)
                });
                parent.children.splice(parent.children.indexOf(node), 1, ...replacement);
            }
        };
}
export function SourceText({ text, quote = '' }) {
    const root = useRef(null);
    const [found, setFound] = useState(false);
    const safeText = useMemo(()=>DOMPurify.sanitize(text, {
            ALLOWED_TAGS: [
                'b',
                'strong',
                'i',
                'em',
                'u',
                'br',
                'table',
                'thead',
                'tbody',
                'tr',
                'td',
                'th',
                'p',
                'div',
                'span',
                'ul',
                'ol',
                'li',
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6'
            ],
            ALLOWED_ATTR: [
                'colspan',
                'rowspan'
            ]
        }), [
        text
    ]);
    const highlight = useMemo(()=>highlightSourceQuote(quote), [
        quote
    ]);
    useEffect(()=>{
        const match = root.current?.querySelector('mark[data-citation-highlight]');
        setFound(!!match);
        match?.scrollIntoView?.({
            block: 'center'
        });
    }, [
        text,
        quote
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            quote ? /*#__PURE__*/ _jsx("output", {
                className: "block text-sm text-muted-foreground",
                children: found ? 'Cited passage highlighted below.' : 'The quoted passage could not be located in this source text.'
            }) : null,
            /*#__PURE__*/ _jsx("div", {
                ref: root,
                className: "prose dark:prose-invert max-w-none break-words",
                children: /*#__PURE__*/ _jsx(ReactMarkdown, {
                    remarkPlugins: [
                        remarkGfm
                    ],
                    rehypePlugins: [
                        rehypeRaw,
                        highlight
                    ],
                    components: {
                        img: ({ alt })=>/*#__PURE__*/ _jsx("span", {
                                children: alt
                            })
                    },
                    children: safeText
                })
            })
        ]
    });
}
